// Requests that waited past the org's escalation limit, and what became of them.
// Amber while still waiting (it is waiting on someone); a closed one says how.
import type { CSSProperties } from 'react';
import { Avatar, Badge, Mono, type ToneName } from './primitives';
import { PCard } from './people';
import { fmtDayKey, orgDateKey } from '../lib/dates';
import { useOrgDates } from '../lib/orgSettings';
import type { Escalation } from '../lib/escalations';

const WHAT: Record<Escalation['subject_type'], string> = {
  leave: 'Leave request',
  correction: 'Time correction',
};

// The product's words (docs/UI-CONVENTIONS.md, Vocabulary): rejected is only the stored value.
const OUTCOME: Record<NonNullable<Escalation['resolution']>, { tone: ToneName; text: string }> = {
  approved: { tone: 'success', text: 'approved' },
  rejected: { tone: 'neutral', text: 'declined' },
  withdrawn: { tone: 'neutral', text: 'withdrawn' },
};

const ROW: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 'var(--hf-space-2) var(--hf-space-3)',
  padding: 'var(--hf-space-2) 0',
  borderBottom: '1px solid var(--hf-rule)',
  fontSize: 'var(--hf-text-sm)',
};

export function EscalationsCard({ rows }: { rows: Escalation[] }) {
  const { tz } = useOrgDates();
  if (!rows.length) return null;
  const open = rows.filter((r) => !r.resolved_at).length;
  const day = (instant: string) => fmtDayKey(orgDateKey(instant, tz));

  return (
    <PCard
      label="Escalations"
      // A facet of the list, dimmed: the tab's AttentionCount is the queue's one
      // number (UI-CONVENTIONS §1), and the waiting requests are already in it.
      right={open > 0 && <Mono dim>{open} still waiting</Mono>}
    >
      {rows.map((r) => {
        const out = r.resolution ? OUTCOME[r.resolution] : null;
        return (
          <div key={r.id} style={ROW}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--hf-space-2)',
                flex: '1 1 10rem',
                minWidth: 0,
              }}
            >
              <Avatar name={r.full_name ?? undefined} size={26} />
              <span style={{ color: 'var(--hf-ink)', overflowWrap: 'anywhere' }}>
                {r.full_name ?? 'Someone'}
              </span>
            </span>
            <span
              style={{
                display: 'inline-flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 'var(--hf-space-2)',
                flex: '2 1 16rem',
                minWidth: 0,
                color: 'var(--hf-ink-2)',
              }}
            >
              {WHAT[r.subject_type]}
              <span style={{ color: 'var(--hf-ink-3)' }}>
                filed {fmtDayKey(r.filed_on)} · escalated {day(r.escalated_at)}
              </span>
              {out && r.resolved_at ? (
                <Badge tone={out.tone}>
                  {out.text} {day(r.resolved_at)}
                </Badge>
              ) : (
                <Badge tone="warning" dot>
                  waiting
                </Badge>
              )}
            </span>
          </div>
        );
      })}
    </PCard>
  );
}
